"use client";

import React from "react";
import { PhoneCall, CalendarCheck, ShieldAlert, Clock, MapPin } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function EmergencyAction() {
  const shouldReduceMotion = useReducedMotion();
  const trustBadges = [{
    label: "24/7 Live dispatch",
    icon: Clock
  }, {
    label: "Direct local crews",
    icon: MapPin
  }, {
    label: "Insurance billing assistance",
    icon: ShieldAlert
  }];
  return <section id="emergencycalltoaction" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-20 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-50px"
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative mx-auto max-w-5xl rounded-2xl border border-border bg-card p-8 sm:p-12 lg:p-16 shadow-lg">
          <div className="flex flex-col items-center text-center">
            {/* Urgent status badge */}
            <Badge variant="outline" className="mb-4 inline-flex items-center gap-2 border-primary/20 bg-muted px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex size-2 rounded-full bg-primary" />
              </span>
              24/7 Emergency response
            </Badge>

            {/* Headline */}
            <h2 className="max-w-2xl font-default text-3xl font-bold tracking-tight text-card-foreground sm:text-4xl lg:text-5xl">
              Need immediate disaster restoration?
            </h2>

            {/* Supporting Copy */}
            <p className="mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
              Water, fire, or mold emergencies require rapid stabilization. Speak directly with our on-call local crews in Maryland, DC, or Northern Virginia without routing through a call center.
            </p>

            {/* CTA Buttons */}
            <div className="mt-8 flex w-full flex-col items-center justify-center gap-4 sm:w-auto sm:flex-row">
              {/* Phone call CTA using plain <a> tag for tel: protocol */}
              <Button asChild size="lg" className="w-full bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 sm:w-auto text-base font-semibold px-6 py-6 h-auto">
                <a href="tel:8554356631" className="inline-flex items-center justify-center gap-2.5">
                  <PhoneCall className="size-5 shrink-0" />
                  <span>Call 855-435-6631</span>
                </a>
              </Button>

              {/* Request service CTA using project Link component */}
              <Button asChild variant="outline" size="lg" className="w-full border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground sm:w-auto text-base font-semibold px-6 py-6 h-auto">
                <Link to="/ContactRequestServicePage" className="inline-flex items-center justify-center gap-2.5">
                  <CalendarCheck className="size-5 shrink-0" />
                  <span>Request Service</span>
                </Link>
              </Button>
            </div>

            {/* Trust points */}
            <div className="mt-10 grid w-full max-w-3xl grid-cols-1 gap-4 border-t border-border pt-8 sm:grid-cols-3">
              {trustBadges.map((badge, index) => {
              const IconComponent = badge.icon;
              return <div key={badge.label} data-index={index} className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <IconComponent className="size-4 text-primary shrink-0" />
                    <span>{badge.label}</span>
                  </div>;
            })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}